import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  Alert,
  Button,
  Breadcrumbs,
  Link,
  Chip,
  IconButton,
  Tooltip,
  Stack
} from '@mui/material';
import {
  Map,
  LocationOn,
  Group,
  Assessment,
  TrendingUp,
  MonitorHeart,
  ArrowBack,
  Refresh,
  Download,
  Share,
  Info,
  Warning,
  CheckCircle
} from '@mui/icons-material';
import UKMap from '../components/maps/UKMap';

const UKMapPage = () => {
  const navigate = useNavigate();
  const { user, token } = useSelector((state) => state.auth);
  const [isLoading, setIsLoading] = useState(true);
  const [insights, setInsights] = useState({
    totalUsers: 0,
    usersWithData: 0,
    usersWithoutData: 0,
    conversionRate: 0,
    topRegions: [],
    hypertensionStats: {
      lowRisk: 0,
      mediumRisk: 0,
      highRisk: 0
    }
  });

  useEffect(() => {
    // Simulate loading data
    const timer = setTimeout(() => {
      setInsights({
        totalUsers: 15,
        usersWithData: 10,
        usersWithoutData: 5,
        conversionRate: 66.7,
        topRegions: [
          { name: 'London', users: 3, conversion: 100 },
          { name: 'Manchester', users: 2, conversion: 50 },
          { name: 'Scotland', users: 2, conversion: 100 },
          { name: 'Midlands', users: 4, conversion: 75 },
          { name: 'North', users: 4, conversion: 50 }
        ],
        hypertensionStats: {
          lowRisk: 5,
          mediumRisk: 4,
          highRisk: 1
        }
      });
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const handleExportData = () => {
    // Mock export functionality
    const data = {
      insights,
      exportDate: new Date().toISOString(),
      exportedBy: user?.email
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `uk-map-analytics-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'CheckYourMate UK User Distribution',
          text: `UK User Distribution: ${insights.totalUsers} total users, ${insights.usersWithData} with data`,
          url: window.location.href
        });
      } catch (err) {
        console.log('Error sharing:', err);
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
    }
  };

  const getHypertensionInsights = () => {
    const total = insights.hypertensionStats.lowRisk + insights.hypertensionStats.mediumRisk + insights.hypertensionStats.highRisk;
    if (total === 0) return [];

    return [
      {
        level: 'Low Risk',
        count: insights.hypertensionStats.lowRisk,
        percentage: ((insights.hypertensionStats.lowRisk / total) * 100).toFixed(1),
        color: 'success',
        icon: <CheckCircle />
      },
      {
        level: 'Medium Risk',
        count: insights.hypertensionStats.mediumRisk,
        percentage: ((insights.hypertensionStats.mediumRisk / total) * 100).toFixed(1),
        color: 'warning',
        icon: <Warning />
      },
      {
        level: 'High Risk',
        count: insights.hypertensionStats.highRisk,
        percentage: ((insights.hypertensionStats.highRisk / total) * 100).toFixed(1),
        color: 'error',
        icon: <Warning />
      }
    ];
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link 
            color="inherit" 
            href="#" 
            onClick={() => navigate('/dashboard')}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            Dashboard
          </Link>
          <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
            <Map sx={{ mr: 0.5 }} />
            UK User Distribution
          </Typography>
        </Breadcrumbs>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
              UK User Distribution Map
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Track user sign-ups and hypertension data across the UK
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh Data">
              <IconButton onClick={() => window.location.reload()}>
                <Refresh />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export Data">
              <IconButton onClick={handleExportData}>
                <Download />
              </IconButton>
            </Tooltip>
            <Tooltip title="Share">
              <IconButton onClick={handleShare}>
                <Share />
              </IconButton>
            </Tooltip>
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={() => navigate('/dashboard')}
            >
              Back to Dashboard
            </Button>
          </Stack>
        </Box>
      </Box>

      {/* Key Insights */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Group sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {insights.totalUsers}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Users
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <MonitorHeart sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {insights.usersWithData}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Users with Data
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Assessment sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {insights.usersWithoutData}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                No Data Yet
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <TrendingUp sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {insights.conversionRate}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Conversion Rate
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Alerts */}
      <Box sx={{ mb: 3 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Focus Areas:</strong> Consider targeted outreach in regions with high user sign-ups but low data conversion rates.
          </Typography>
        </Alert>
        
        {insights.hypertensionStats.highRisk > 0 && (
          <Alert severity="warning">
            <Typography variant="body2">
              <strong>Health Alert:</strong> {insights.hypertensionStats.highRisk} users have high hypertension risk. Consider prioritizing these users for healthcare provider outreach.
            </Typography>
          </Alert>
        )}
      </Box>

      {/* Main Map */}
      <Paper elevation={3} sx={{ p: 2, mb: 3 }}>
        <UKMap />
      </Paper>

      {/* Additional Analytics */}
      <Grid container spacing={3}>
        {/* Regional Performance */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Regional Performance
            </Typography>
            {insights.topRegions.map((region, index) => (
              <Box key={index} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body1">{region.name}</Typography>
                  <Chip 
                    label={`${region.users} users`} 
                    color="primary" 
                    size="small"
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: '100%', mr: 1 }}>
                    <Box
                      sx={{
                        height: 8,
                        backgroundColor: 'grey.300',
                        borderRadius: 4,
                        position: 'relative'
                      }}
                    >
                      <Box
                        sx={{
                          height: '100%',
                          backgroundColor: region.conversion >= 75 ? 'success.main' : region.conversion >= 50 ? 'warning.main' : 'error.main',
                          borderRadius: 4,
                          width: `${region.conversion}%`
                        }}
                      />
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {region.conversion}%
                  </Typography>
                </Box>
              </Box>
            ))}
          </Paper>
        </Grid>

        {/* Hypertension Risk Distribution */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Hypertension Risk Distribution
            </Typography>
            {getHypertensionInsights().map((insight, index) => (
              <Box key={index} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {insight.icon}
                    <Typography variant="body1" sx={{ ml: 1 }}>
                      {insight.level}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {insight.count} users
                    </Typography>
                    <Chip 
                      label={`${insight.percentage}%`} 
                      color={insight.color} 
                      size="small"
                    />
                  </Box>
                </Box>
              </Box>
            ))}
            
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                Early detection and intervention can significantly reduce hypertension-related complications.
              </Typography>
            </Alert>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default UKMapPage; 